// src/pages/Header.tsx
import { useState, useEffect, useRef } from "react";
import logo from "../assets/logo.png";
import { Link, NavLink, useNavigate } from "react-router-dom";
import Image from "../shared/image/Image";
import {
  ArrowUpRightIcon,
  ListIcon,
  XIcon,
  CaretDownIcon,
} from "@phosphor-icons/react/dist/ssr";
import Button from "../shared/button/Button";

const useCases = [
  { to: "/use-cases/technical-ai-recruiter", label: "Technical AI Recruiter" },
  { to: "/use-cases/ai-campus-drives", label: "AI Campus Drives" },
  {
    to: "/use-cases/high-volume-recruiter",
    label: "High Volume Recruiter",
  },
  { to: "/use-cases/diversity-hiring", label: "Diversity Hiring" },
  {
    to: "/use-cases/universal-ai-recruiter",
    label: "Universal AI Recruiter",
  },
];

const productCategories = [
  {
    title: "Products",
    items: [
      { to: "/product/ai-interviews", label: "AI Interviews" },
      { to: "/product/ai-phone-screener", label: "AI Phone Screener" },
      { to: "/product/jd-cv-matching", label: "JD CV Matching" },
      { to: "/product/coding-assessment", label: "Coding Assessment" },
      { to: "/product/ai-proctoring", label: "AI Proctoring" },
    ],
  },
  {
    title: "Platform & Integrations",
    items: [
      { to: "/product/languages", label: "Multi-Language Support" },
      { to: "/product/ats-integrations", label: "ATS Integrations" },
      { to: "/product/whatsapp-dashboard", label: "WhatsApp Dashboard" },
    ],
  },
];

const links = [{ to: "/pricing", label: "Pricing" }];

const developerLinks = [
  { to: "/api", label: "Babblebots API" },
  { to: "https://magenta-ladybug-395.notion.site/Babblebots-External-Partner-Integration-b41f64a7011a4657829bada2ec484e53", label: "Documentation", external: true },
];

const companyLinks = [
  { to: "/customers", label: "Customers" },
  { to: "/resources", label: "Resources" },
  { to: "/resources/aboutus", label: "About Us" },
  { to: "/resources/contact", label: "Contact" },
];

// ─── Desktop Dropdown (hover-based) ───────────────────────────────────────────

const DropdownMenu = ({
  title,
  categories,
}: {
  title: string;
  categories: { title: string; items: { to: string; label: string; external?: boolean }[] }[];
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const open = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const close = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const isSingleColumn =
    categories.length === 1 && categories[0].items.length <= 6;

  return (
    <div
      className="relative"
      ref={menuRef}
      onMouseEnter={open}
      onMouseLeave={close}
    >
      <button
        className={`flex items-center gap-1 py-2 text-sm font-medium transition-colors ${
          isOpen ? "text-app-300" : "text-gray-300 hover:text-white"
        }`}
      >
        {title}
        <CaretDownIcon
          size={14}
          weight="bold"
          className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      <div
        className={`absolute left-1/2 top-full z-50 -translate-x-1/2 pt-2 transition-all duration-200 ${
          isOpen
            ? "pointer-events-auto translate-y-0 opacity-100"
            : "pointer-events-none -translate-y-1 opacity-0"
        }`}
      >
        <div
          className={`rounded-xl border border-gray-600 bg-gray-800/95 p-5 shadow-2xl backdrop-blur-xl ${
            isSingleColumn ? "min-w-[240px]" : "min-w-[480px]"
          }`}
        >
          <div
            className={`grid gap-6 ${
              isSingleColumn ? "grid-cols-1" : "grid-cols-2"
            }`}
          >
            {categories.map((category) => (
              <div key={category.title}>
                {categories.length > 1 && (
                  <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-app-300/70">
                    {category.title}
                  </h3>
                )}
                <ul className="space-y-0.5">
                  {category.items.map((item) => (
                    <li key={item.to}>
                      {item.external ? (
                        <a
                          href={item.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setIsOpen(false)}
                          className="block rounded-lg px-3 py-2 text-sm text-gray-300 transition-colors hover:bg-white/5 hover:text-white"
                        >
                          {item.label}
                        </a>
                      ) : (
                        <Link
                          to={item.to}
                          onClick={() => setIsOpen(false)}
                          className="block rounded-lg px-3 py-2 text-sm text-gray-300 transition-colors hover:bg-white/5 hover:text-white"
                        >
                          {item.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Mobile Menu ──────────────────────────────────────────────────────────────

const MobileMenu = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleBookDemo = () => {
    navigate("/book-demo");
    onClose();
  };

  const toggleSection = (section: string) =>
    setExpandedSection(expandedSection === section ? null : section);

  if (!isOpen) return null;

  return (
    <div className="border-t border-gray-700 bg-gray-900 pb-6">
      {/* Products */}
      <div className="border-b border-gray-700/50">
        <button
          onClick={() => toggleSection("products")}
          className="flex w-full items-center justify-between px-6 py-3.5 text-sm font-medium text-gray-200"
        >
          Products
          <CaretDownIcon
            size={14}
            weight="bold"
            className={`transition-transform duration-200 ${
              expandedSection === "products" ? "rotate-180" : ""
            }`}
          />
        </button>
        {expandedSection === "products" && (
          <div className="px-6 pb-3">
            {productCategories.map((category) => (
              <div key={category.title} className="mb-3">
                <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-widest text-app-300/60">
                  {category.title}
                </h4>
                {category.items.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={onClose}
                    className="block py-2 pl-3 text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Use Cases */}
      <div className="border-b border-gray-700/50">
        <button
          onClick={() => toggleSection("useCases")}
          className="flex w-full items-center justify-between px-6 py-3.5 text-sm font-medium text-gray-200"
        >
          Use Cases
          <CaretDownIcon
            size={14}
            weight="bold"
            className={`transition-transform duration-200 ${
              expandedSection === "useCases" ? "rotate-180" : ""
            }`}
          />
        </button>
        {expandedSection === "useCases" && (
          <div className="px-6 pb-3">
            {useCases.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={onClose}
                className="block py-2 pl-3 text-sm text-gray-400 transition-colors hover:text-white"
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Regular Links */}
      {links.map((link) => (
        <Link
          key={link.label}
          to={link.to}
          onClick={onClose}
          className="block border-b border-gray-700/50 px-6 py-3.5 text-sm font-medium text-gray-200"
        >
          {link.label}
        </Link>
      ))}

      {/* Developers */}
      <div className="border-b border-gray-700/50">
        <button
          onClick={() => toggleSection("developers")}
          className="flex w-full items-center justify-between px-6 py-3.5 text-sm font-medium text-gray-200"
        >
          Developers
          <CaretDownIcon
            size={14}
            weight="bold"
            className={`transition-transform duration-200 ${
              expandedSection === "developers" ? "rotate-180" : ""
            }`}
          />
        </button>
        {expandedSection === "developers" && (
          <div className="px-6 pb-3">
            {developerLinks.map((item) =>
              item.external ? (
                <a
                  key={item.to}
                  href={item.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                  className="block py-2 pl-3 text-sm text-gray-400 transition-colors hover:text-white"
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={onClose}
                  className="block py-2 pl-3 text-sm text-gray-400 transition-colors hover:text-white"
                >
                  {item.label}
                </Link>
              )
            )}
          </div>
        )}
      </div>

      {/* Company */}
      <div className="border-b border-gray-700/50">
        <button
          onClick={() => toggleSection("company")}
          className="flex w-full items-center justify-between px-6 py-3.5 text-sm font-medium text-gray-200"
        >
          Company
          <CaretDownIcon
            size={14}
            weight="bold"
            className={`transition-transform duration-200 ${
              expandedSection === "company" ? "rotate-180" : ""
            }`}
          />
        </button>
        {expandedSection === "company" && (
          <div className="px-6 pb-3">
            {companyLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={onClose}
                className="block py-2 pl-3 text-sm text-gray-400 transition-colors hover:text-white"
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="mt-5 flex flex-col gap-3 px-6">
        <Link
          to="/ai-talent-drive"
          onClick={onClose}
          className="rounded-full px-5 py-3 text-center text-sm font-semibold text-white"
          style={{
            background: "linear-gradient(90deg, #FF505F, #FF8A3D, #FF505F)",
            backgroundSize: "200% 100%",
            animation: "mtw-nav-shimmer 3s linear infinite",
            boxShadow: "0 0 16px rgba(255,80,95,0.35)",
          }}
        >
          AI Jobs Drive
        </Link>
        <Button
          variant="secondary"
          size="base"
          className="flex-row-reverse hover:gap-6"
          icon={<ArrowUpRightIcon size={18} />}
          onClick={handleBookDemo}
        >
          Book a demo
        </Button>
        <a href="https://app.babblebots.ai" target="_blank" rel="noreferrer">
          <Button
            variant="primary"
            size="base"
            className="w-full flex-row-reverse"
            icon={<ArrowUpRightIcon size={18} />}
          >
            Sign in to get started
          </Button>
        </a>
      </div>
    </div>
  );
};

// ─── Main Header ──────────────────────────────────────────────────────────────

function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleBookDemo = () => navigate("/book-demo");

  return (
    <header
      className={`fixed top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-black/80 shadow-[0_4px_30px_rgba(0,0,0,0.5)] backdrop-blur-xl"
          : "shadow-[0_1px_12px_rgba(0,0,0,0.3)]"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6 lg:h-[72px] lg:px-8">
        {/* Logo */}
        <Link
          to="/"
          className="shrink-0"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <Image
            src={logo}
            className="h-8 w-auto object-contain lg:h-9"
            alt="Babblebots.AI Logo"
          />
        </Link>

        {/* Desktop Nav */}
        {screenWidth >= 768 ? (
          <>
            <nav className="flex items-center gap-5 lg:gap-7">
              <DropdownMenu title="Products" categories={productCategories} />

              <DropdownMenu
                title="Use Cases"
                categories={[{ title: "Use Cases", items: useCases }]}
              />
              <DropdownMenu
                title="Developers"
                categories={[{ title: "Developers", items: developerLinks }]}
              />
              {links.map((link) => (
                <NavLink
                  key={link.label}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors ${
                      isActive
                        ? "text-app-300"
                        : "text-gray-300 hover:text-white"
                    }`
                  }
                  to={link.to}
                >
                  {link.label}
                </NavLink>
              ))}

              <DropdownMenu
                title="Company"
                categories={[{ title: "Company", items: companyLinks }]}
              />

              <NavLink
                to="/ai-talent-drive"
                className="group flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold text-white transition-all duration-300 hover:scale-105"
                style={{
                  background: "linear-gradient(90deg, #FF505F, #FF8A3D, #FF505F)",
                  backgroundSize: "200% 100%",
                  animation: "mtw-nav-shimmer 3s linear infinite",
                  boxShadow: "0 0 16px rgba(255,80,95,0.4)",
                }}
              >
                AI Jobs Drive
              </NavLink>
            </nav>

            <div className="flex items-center gap-3">
              <button
                onClick={handleBookDemo}
                className="group flex items-center gap-2 rounded-full border border-gray-500 px-4 py-2 text-sm font-medium text-white transition-all duration-300 hover:gap-3 hover:border-white lg:px-5"
              >
                Book a demo
                <ArrowUpRightIcon
                  size={16}
                  className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </button>
              <a
                href="https://app.babblebots.ai"
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-2 rounded-full bg-app-300 px-4 py-2 text-sm font-semibold text-gray-900 transition-all duration-300 hover:bg-app-200 hover:shadow-[0_0_20px_rgba(99,205,220,0.3)] lg:px-5"
              >
                Customer Login
                <ArrowUpRightIcon
                  size={16}
                  className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </a>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2.5">
            {/* Same campaign button as the desktop nav, kept in the bar itself
                so it is visible without opening the menu. */}
            <NavLink
              to="/ai-talent-drive"
              onClick={() => setIsMobileMenuOpen(false)}
              className="whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold text-white"
              style={{
                background: "linear-gradient(90deg, #FF505F, #FF8A3D, #FF505F)",
                backgroundSize: "200% 100%",
                animation: "mtw-nav-shimmer 3s linear infinite",
                boxShadow: "0 0 14px rgba(255,80,95,0.4)",
              }}
            >
              AI Jobs Drive
            </NavLink>
            <button
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-300 transition-colors hover:bg-white/5 hover:text-white"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <XIcon size={22} weight="bold" />
              ) : (
                <ListIcon size={22} weight="bold" />
              )}
            </button>
          </div>
        )}
      </div>

      {/* Mobile Menu */}
      {screenWidth < 768 && (
        <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        />
      )}
    </header>
  );
}

export default Header;
